export type MergeFieldValueType =
  | "text"
  | "multiline_text"
  | "date"
  | "email"
  | "phone"
  | "currency"
  | "number"
  | "checkbox"
  | "image_url"

export type MergeFieldGroup =
  | "deal"
  | "party"
  | "vehicle"
  | "dealership"
  | "store"
  | "finance"
  | "custom"
  | "manual"

export type MergeFieldSourceKind =
  | "standard"
  | "dealCustomField"
  | "dealershipCustomField"
  | "manual"
  | "importColumn"
  | "apiProvided"

export type MergeFieldPrimarySource = MergeFieldSourceKind

export type MergeFieldTemplateFieldType =
  | "text"
  | "image"
  | "signature"
  | "date"
  | "checkbox"
  | "select"
  | "initial"

export type MergeFieldDefinition = {
  key: string
  label: string
  description?: string
  group: MergeFieldGroup
  valueType: MergeFieldValueType
  sourceKind: MergeFieldSourceKind
  supportsSharedTemplates: boolean
  deprecated?: boolean
  enumOptions?: Array<{
    value: string
    label: string
  }>
}

export type MergeFieldPartyAddressType =
  | "mailing"
  | "registration"
  | "residence"
  | "business"
  | "billing"
  | "shipping"
  | "physical"

export type MergeFieldPartyPhoneType = "mobile" | "home" | "work" | "business" | "other"

export type MergeFieldTransform =
  | {
      type: "char_at"
      index: number
    }
  | {
      type: "slice"
      start: number
      end?: number
    }
  | {
      type: "uppercase"
    }
  | {
      type: "lowercase"
    }
  | {
      type: "trim"
    }

export type MergeFieldBinding = {
  definitionKey: string
  primarySource: MergeFieldPrimarySource
  partySelector?: string
  addressType?: MergeFieldPartyAddressType
  phoneType?: MergeFieldPartyPhoneType
  fallbackSources?: Array<"manual" | "importColumn" | "apiProvided">
  allowOverride?: boolean
  requiredAtSend?: boolean
  fallback?: "blank" | "manual"
  defaultValue?: string
  customFieldKey?: string
  importColumnKey?: string
  transform?: MergeFieldTransform
}

export type CustomMergeFieldDefinition = {
  _id: string
  scope: "global" | "dealership"
  dealershipId?: string
  key: string
  label: string
  description?: string
  sourceKind: "dealCustomField" | "dealershipCustomField" | "manual"
  valueType: MergeFieldValueType
  defaultValue?: string
  customFieldKey?: string
  createdAt: number
  updatedAt: number
}

export type MergeFieldCompatibilityStatus = "compatible" | "warning" | "blocked"

export type MergeFieldCompatibility = {
  status: MergeFieldCompatibilityStatus
  reason?: string
}

export type MergeFieldCompatibilityOptions = {
  checkboxMode?: "boolean" | "single_choice"
}

export type PartyMergeDefinitionInfo = {
  attributePath: string
  addressPart?: "street" | "city" | "state" | "zip_code"
  addressType?: MergeFieldPartyAddressType
  supportsAddressType: boolean
  supportsPhoneType: boolean
}

export const MERGE_FIELD_TRANSFORM_LABELS: Record<MergeFieldTransform["type"], string> = {
  char_at: "Character at Position",
  slice: "Slice Text",
  uppercase: "Uppercase",
  lowercase: "Lowercase",
  trim: "Trim Whitespace",
}

export const MERGE_FIELD_GROUP_LABELS: Record<MergeFieldGroup, string> = {
  deal: "Deal",
  party: "Buyer / Party",
  vehicle: "Vehicle",
  dealership: "Dealership",
  store: "Store",
  finance: "Finance",
  custom: "Custom",
  manual: "Dealership Entered",
}

export const MERGE_FIELD_PARTY_ADDRESS_TYPE_LABELS: Record<MergeFieldPartyAddressType, string> = {
  mailing: "Mailing",
  registration: "Registration",
  residence: "Residence",
  business: "Business",
  billing: "Billing",
  shipping: "Shipping",
  physical: "Physical",
}

export const MERGE_FIELD_PARTY_PHONE_TYPE_LABELS: Record<MergeFieldPartyPhoneType, string> = {
  mobile: "Mobile",
  home: "Home",
  work: "Work",
  business: "Business",
  other: "Other",
}

export const MERGE_FIELD_PARTY_ADDRESS_TYPE_OPTIONS = Object.entries(
  MERGE_FIELD_PARTY_ADDRESS_TYPE_LABELS,
).map(([value, label]) => ({
  value: value as MergeFieldPartyAddressType,
  label,
}))

export const MERGE_FIELD_PARTY_PHONE_TYPE_OPTIONS = Object.entries(
  MERGE_FIELD_PARTY_PHONE_TYPE_LABELS,
).map(([value, label]) => ({
  value: value as MergeFieldPartyPhoneType,
  label,
}))

export function parsePartyMergeDefinitionKey(
  definitionKey: string,
): PartyMergeDefinitionInfo | undefined {
  const match = definitionKey.match(/^party\.(.+)$/)
  if (!match) {
    return undefined
  }

  const attributePath = match[1]
  if (!attributePath) {
    return undefined
  }

  const genericAddressMatch = attributePath.match(/^address\.(street|city|state|zip_code)$/)
  if (genericAddressMatch) {
    return {
      attributePath,
      addressPart: genericAddressMatch[1] as PartyMergeDefinitionInfo["addressPart"],
      supportsAddressType: true,
      supportsPhoneType: false,
    }
  }

  if (attributePath === "phone") {
    return {
      attributePath,
      supportsAddressType: false,
      supportsPhoneType: true,
    }
  }

  return {
    attributePath,
    supportsAddressType: false,
    supportsPhoneType: false,
  }
}

export function isPartyMergeDefinitionKey(definitionKey: string) {
  return parsePartyMergeDefinitionKey(definitionKey) !== undefined
}

export function mergeFieldSupportsPartyAddressType(definitionKey: string) {
  return parsePartyMergeDefinitionKey(definitionKey)?.supportsAddressType === true
}

export function getFixedPartyAddressTypeForDefinition(definitionKey: string) {
  return parsePartyMergeDefinitionKey(definitionKey)?.addressType
}

export function mergeFieldSupportsPartyPhoneType(definitionKey: string) {
  return parsePartyMergeDefinitionKey(definitionKey)?.supportsPhoneType === true
}

export const STANDARD_MERGE_FIELDS: MergeFieldDefinition[] = [
  {
    key: "deal.stock_number",
    label: "Stock Number",
    description: "The deal's stock number.",
    group: "deal",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "deal.sale_date",
    label: "Sale Date",
    description: "The date the deal was sold.",
    group: "deal",
    valueType: "date",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "deal.delivery_date",
    label: "Delivery Date",
    description: "The date the vehicle is delivered.",
    group: "deal",
    valueType: "date",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "deal.registering_state",
    label: "Registering State",
    description: "The state where the vehicle will be registered.",
    group: "deal",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "party.full_name",
    label: "Party Full Name",
    description: "The selected buyer party's full legal name.",
    group: "party",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "party.first_name",
    label: "Party First Name",
    description: "The selected buyer party's first name.",
    group: "party",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "party.last_name",
    label: "Party Last Name",
    description: "The selected buyer party's last name.",
    group: "party",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "party.email",
    label: "Party Email",
    description: "The selected buyer party's email address.",
    group: "party",
    valueType: "email",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "party.phone",
    label: "Party Phone",
    description: "The selected buyer party's phone number.",
    group: "party",
    valueType: "phone",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "party.address.street",
    label: "Party Street",
    description: "The selected buyer party's street for the chosen address type.",
    group: "party",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "party.address.city",
    label: "Party Address City",
    description: "The city for the chosen address type on the selected buyer party.",
    group: "party",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "party.address.state",
    label: "Party Address State",
    description: "The state for the chosen address type on the selected buyer party.",
    group: "party",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "party.address.zip_code",
    label: "Party Address ZIP",
    description: "The ZIP code for the chosen address type on the selected buyer party.",
    group: "party",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "party.date_of_birth",
    label: "Party Date of Birth",
    description: "The selected buyer party's date of birth.",
    group: "party",
    valueType: "date",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "party.drivers_license_number",
    label: "Party Driver License Number",
    description: "The selected buyer party's driver license number.",
    group: "party",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "party.drivers_license_state",
    label: "Party Driver License State",
    description: "The selected buyer party's driver license issuing state.",
    group: "party",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "party.drivers_license_expiration",
    label: "Party Driver License Expiration",
    description: "The selected buyer party's driver license expiration date.",
    group: "party",
    valueType: "date",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "party.ein",
    label: "Party EIN",
    description: "The selected buyer party's EIN when the party is a business.",
    group: "party",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "party.legal_business_name",
    label: "Party Legal Business Name",
    description: "The selected buyer party's legal business name when the party is a business.",
    group: "party",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "party.authorized_signer_name",
    label: "Party Authorized Signer Name",
    description: "The authorized signer name for the selected business buyer party.",
    group: "party",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "party.authorized_signer_title",
    label: "Party Authorized Signer Title",
    description: "The authorized signer title for the selected business buyer party.",
    group: "party",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "vehicle.sale.vin",
    label: "Selling Vehicle VIN",
    description: "The VIN for the primary selling vehicle.",
    group: "vehicle",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "vehicle.sale.stock_number",
    label: "Selling Vehicle Stock Number",
    description: "The stock number for the primary selling vehicle.",
    group: "vehicle",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "vehicle.sale.year",
    label: "Selling Vehicle Year",
    description: "The model year for the primary selling vehicle.",
    group: "vehicle",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "vehicle.sale.make",
    label: "Selling Vehicle Make",
    description: "The make for the primary selling vehicle.",
    group: "vehicle",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "vehicle.sale.model",
    label: "Selling Vehicle Model",
    description: "The model for the primary selling vehicle.",
    group: "vehicle",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "vehicle.sale.year_make_model",
    label: "Selling Vehicle Year / Make / Model",
    description: "Combined year, make, and model for the primary selling vehicle.",
    group: "vehicle",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "vehicle.fuel_type",
    label: "Vehicle Fuel Type",
    description: "Common fuel type options for vehicle-related dropdowns.",
    group: "vehicle",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
    enumOptions: [
      { value: "gasoline", label: "Gasoline" },
      { value: "diesel", label: "Diesel" },
      { value: "electric", label: "Electric" },
      { value: "hybrid", label: "Hybrid" },
      { value: "plug_in_hybrid", label: "Plug-In Hybrid" },
      { value: "flex_fuel", label: "Flex Fuel" },
      { value: "cng", label: "Compressed Natural Gas" },
      { value: "lpg", label: "Propane / LPG" },
      { value: "hydrogen", label: "Hydrogen" },
      { value: "other", label: "Other" },
    ],
  },
  {
    key: "vehicle.sale.odometer",
    label: "Selling Vehicle Odometer",
    description: "The odometer reading for the primary selling vehicle.",
    group: "vehicle",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "vehicle.sale.odometer_status",
    label: "Selling Vehicle Odometer Status",
    description: "The odometer status for the primary selling vehicle.",
    group: "vehicle",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
    enumOptions: [
      { value: "actual", label: "Actual Mileage" },
      { value: "exceeds_limits", label: "Mileage Exceeds Mechanical Limits" },
      { value: "true_mileage_unknown", label: "Not Actual Mileage / Discrepancy" },
      { value: "exempt", label: "Exempt" },
    ],
  },
  {
    key: "vehicle.sale.title_status",
    label: "Selling Vehicle Title Status",
    description: "The title status for the primary selling vehicle.",
    group: "vehicle",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
    enumOptions: [
      { value: "clean", label: "Clean" },
      { value: "salvage", label: "Salvage" },
      { value: "restored", label: "Restored" },
      { value: "lemon", label: "Lemon" },
      { value: "dismantled", label: "Dismantled" },
      { value: "bonded", label: "Bonded" },
      { value: "other", label: "Other" },
    ],
  },
  {
    key: "vehicle.trade_in.vin",
    label: "Trade Vehicle VIN",
    description: "The VIN for the primary trade vehicle.",
    group: "vehicle",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "vehicle.trade_in.stock_number",
    label: "Trade Vehicle Stock Number",
    description: "The stock number for the primary trade vehicle.",
    group: "vehicle",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "vehicle.trade_in.year",
    label: "Trade Vehicle Year",
    description: "The model year for the primary trade vehicle.",
    group: "vehicle",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "vehicle.trade_in.make",
    label: "Trade Vehicle Make",
    description: "The make for the primary trade vehicle.",
    group: "vehicle",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "vehicle.trade_in.model",
    label: "Trade Vehicle Model",
    description: "The model for the primary trade vehicle.",
    group: "vehicle",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "vehicle.trade_in.odometer",
    label: "Trade Vehicle Odometer",
    description: "The odometer reading for the primary trade vehicle.",
    group: "vehicle",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "vehicle.trade_in.odometer_status",
    label: "Trade Vehicle Odometer Status",
    description: "The odometer status for the primary trade vehicle.",
    group: "vehicle",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
    enumOptions: [
      { value: "actual", label: "Actual Mileage" },
      { value: "exceeds_limits", label: "Mileage Exceeds Mechanical Limits" },
      { value: "true_mileage_unknown", label: "Not Actual Mileage / Discrepancy" },
      { value: "exempt", label: "Exempt" },
    ],
  },
  {
    key: "vehicle.trade_in.title_status",
    label: "Trade Vehicle Title Status",
    description: "The title status for the primary trade vehicle.",
    group: "vehicle",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
    enumOptions: [
      { value: "clean", label: "Clean" },
      { value: "salvage", label: "Salvage" },
      { value: "restored", label: "Restored" },
      { value: "lemon", label: "Lemon" },
      { value: "dismantled", label: "Dismantled" },
      { value: "bonded", label: "Bonded" },
      { value: "other", label: "Other" },
    ],
  },
  {
    key: "vehicle.trade_in.year_make_model",
    label: "Trade Vehicle Year / Make / Model",
    description: "Combined year, make, and model for the primary trade vehicle.",
    group: "vehicle",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "dealership.name",
    label: "Dealership Name",
    description: "The dealership's display name.",
    group: "dealership",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "dealership.logo_url",
    label: "Dealership Logo",
    description: "The dealership-managed logo asset.",
    group: "dealership",
    valueType: "image_url",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "dealership.website",
    label: "Dealership Website",
    description: "The dealership website URL.",
    group: "dealership",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "dealership.address.full",
    label: "Dealership Full Address",
    description: "A composed address block built from dealership address fields.",
    group: "dealership",
    valueType: "multiline_text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "store.name",
    label: "Store Name",
    description: "The store or rooftop name.",
    group: "store",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "store.phone",
    label: "Store Phone",
    description: "The store phone number.",
    group: "store",
    valueType: "phone",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "store.address.full",
    label: "Store Full Address",
    description: "A composed address block built from store address fields.",
    group: "store",
    valueType: "multiline_text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "finance.vehicle_price",
    label: "Vehicle Price",
    description: "The vehicle sale price from deal financials.",
    group: "finance",
    valueType: "currency",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "finance.trade_allowance",
    label: "Trade Allowance",
    description: "The trade-in allowance amount from deal financials.",
    group: "finance",
    valueType: "currency",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "finance.trade_payoff",
    label: "Trade Payoff",
    description: "The trade payoff amount from deal financials.",
    group: "finance",
    valueType: "currency",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "finance.trade_equity",
    label: "Trade Equity",
    description: "Trade allowance minus trade payoff.",
    group: "finance",
    valueType: "currency",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "finance.sales_tax_total",
    label: "Sales Tax Total",
    description: "The total of tax line items.",
    group: "finance",
    valueType: "currency",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "finance.fees_total",
    label: "Fees Total",
    description: "The total of fee line items.",
    group: "finance",
    valueType: "currency",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "finance.subtotal",
    label: "Subtotal",
    description: "The total of debit line items before credits are applied.",
    group: "finance",
    valueType: "currency",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "finance.total_down",
    label: "Total Down",
    description: "The total of down payment, deposit, and payment credits.",
    group: "finance",
    valueType: "currency",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "finance.total_due",
    label: "Total Due",
    description: "The net total after credits are applied to debits.",
    group: "finance",
    valueType: "currency",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "finance.lender_name",
    label: "Lender Name",
    description: "The lender name saved on the deal.",
    group: "finance",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "finance.lender_number",
    label: "Lender Number",
    description: "The lender number used for titling.",
    group: "finance",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "finance.lender_phone",
    label: "Lender Phone",
    description: "The lender phone number saved on the deal.",
    group: "finance",
    valueType: "phone",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "finance.lender_address",
    label: "Lender Address",
    description: "The lender mailing address saved on the deal.",
    group: "finance",
    valueType: "multiline_text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "finance.lender_address.street",
    label: "Lender Address Street",
    description: "The lender mailing street saved on the deal.",
    group: "finance",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "finance.lender_address.city",
    label: "Lender Address City",
    description: "The lender mailing city saved on the deal.",
    group: "finance",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "finance.lender_address.state",
    label: "Lender Address State",
    description: "The lender mailing state saved on the deal.",
    group: "finance",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "finance.lender_address.zip_code",
    label: "Lender Address ZIP Code",
    description: "The lender mailing ZIP code saved on the deal.",
    group: "finance",
    valueType: "text",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "finance.lien_date",
    label: "Lien Date",
    description: "The lien date saved on the deal.",
    group: "finance",
    valueType: "date",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "finance.lien_amount",
    label: "Lien Amount",
    description: "The lien amount saved on the deal.",
    group: "finance",
    valueType: "currency",
    sourceKind: "standard",
    supportsSharedTemplates: true,
  },
  {
    key: "manual.lienholder_name",
    label: "Lender Name",
    description: "Dealership staff enter the lender name before sending.",
    group: "manual",
    valueType: "text",
    sourceKind: "manual",
    supportsSharedTemplates: true,
  },
  {
    key: "manual.lender_number",
    label: "Lender Number",
    description: "Dealership staff enter the lender number before sending.",
    group: "manual",
    valueType: "text",
    sourceKind: "manual",
    supportsSharedTemplates: true,
  },
  {
    key: "manual.trade_payoff_amount",
    label: "Trade Payoff Amount",
    description: "Dealership staff enter the payoff amount before sending.",
    group: "manual",
    valueType: "currency",
    sourceKind: "manual",
    supportsSharedTemplates: true,
  },
]

export function getMergeFieldDefinition(definitionKey: string): MergeFieldDefinition | undefined {
  return STANDARD_MERGE_FIELDS.find((field) => field.key === definitionKey)
}

const VALUE_TYPE_LABELS: Record<MergeFieldValueType, string> = {
  text: "Text",
  multiline_text: "Multi-line Text",
  date: "Date",
  email: "Email",
  phone: "Phone",
  currency: "Currency",
  number: "Number",
  checkbox: "Checkbox",
  image_url: "Image",
}

const SOURCE_KIND_LABELS: Record<string, string> = {
  standard: "Auto-filled",
  dealCustomField: "Per-Deal",
  dealershipCustomField: "Dealership-wide",
  manual: "Dealership Entered",
  importColumn: "Imported",
  apiProvided: "API",
}

const SCOPE_LABELS: Record<string, string> = {
  global: "Shared (All Dealers)",
  dealership: "This Dealership",
}

export function formatValueType(valueType: MergeFieldValueType): string {
  return VALUE_TYPE_LABELS[valueType] ?? valueType
}

export function formatSourceKind(sourceKind: string): string {
  return SOURCE_KIND_LABELS[sourceKind] ?? sourceKind
}

export function formatScope(scope: string): string {
  return SCOPE_LABELS[scope] ?? scope
}

export function formatPartyAddressType(value: MergeFieldPartyAddressType | undefined) {
  return value ? MERGE_FIELD_PARTY_ADDRESS_TYPE_LABELS[value] : "Address Type"
}

export function formatPartyPhoneType(value: MergeFieldPartyPhoneType | undefined) {
  return value ? MERGE_FIELD_PARTY_PHONE_TYPE_LABELS[value] : "Phone Type"
}

export const ALL_VALUE_TYPES: { value: MergeFieldValueType; label: string }[] = Object.entries(
  VALUE_TYPE_LABELS,
).map(([value, label]) => ({
  value: value as MergeFieldValueType,
  label,
}))

export const ALL_SOURCE_KINDS: { value: string; label: string }[] = [
  { value: "manual", label: "Dealership Entered" },
  { value: "dealCustomField", label: "Per-Deal" },
  { value: "dealershipCustomField", label: "Dealership-wide" },
]

export function getMergeFieldCompatibility(
  fieldType: MergeFieldTemplateFieldType,
  valueType: MergeFieldValueType,
  options?: MergeFieldCompatibilityOptions,
): MergeFieldCompatibility {
  if (fieldType === "signature" || fieldType === "initial") {
    return {
      status: "blocked",
      reason: "Signature and initial fields cannot be bound to merge fields.",
    }
  }

  if (fieldType === "image") {
    return valueType === "image_url"
      ? { status: "compatible" }
      : {
          status: "blocked",
          reason: "Image fields can only use image merge values.",
        }
  }

  if (valueType === "image_url") {
    return {
      status: "blocked",
      reason: "Image merge fields are not supported by template field inputs yet.",
    }
  }

  if (fieldType === "checkbox") {
    if (options?.checkboxMode === "single_choice") {
      return {
        status: "compatible",
      }
    }

    return valueType === "checkbox"
      ? { status: "compatible" }
      : {
          status: "blocked",
          reason: "Checkbox fields can only use checkbox merge values.",
        }
  }

  if (fieldType === "date") {
    return valueType === "date"
      ? { status: "compatible" }
      : {
          status: "blocked",
          reason: "Date fields can only use date merge values.",
        }
  }

  if (fieldType === "select") {
    if (valueType === "checkbox") {
      return {
        status: "blocked",
        reason: "Select fields can only use scalar text-like merge values.",
      }
    }

    return { status: "compatible" }
  }

  if (valueType === "checkbox") {
    return {
      status: "blocked",
      reason: "Checkbox merge values cannot be used in text or date fields.",
    }
  }

  if (valueType === "date") {
    return {
      status: "warning",
      reason: "Date values will be formatted as text in a text field.",
    }
  }

  return { status: "compatible" }
}
